import React, { useState } from 'react';
import { Container, Box, Typography, Grid2, Paper, Button, Divider, Stack, Chip, Card, CardContent, useTheme, alpha } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowForward,CheckCircle} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { 
  admin_data, organization_data, primaryFeatures, 
  student_data_, tutor_data_, video_data_ 
} from './Helper';
import Footer from './Footer';
import ValidationInsightsSection from './ValidationInsightSection';

// --- INTERNAL COMPONENTS ---

const FeatureSlide = ({ payload, reverse }) => (
  <Box sx={{ py: 6 }}>
    {payload.map((item, index) => {
      const isReversed = index % 2 !== 0 || reverse;
      return (
        <Grid2
          container
          spacing={4}
          alignItems="stretch"
          key={index}
          sx={{ mb: 6, flexDirection: isReversed ? 'row-reverse' : 'row' }}
        >
          {/* Image Card */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: isReversed ? 60 : -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              style={{ height: '100%' }}
            >
              <Paper
                elevation={0}
                sx={{
                  overflow: 'hidden',
                  lineHeight: 0,
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <img
                  src={item.link}
                  alt={item.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </Paper>
            </motion.div>
          </Grid2>

          {/* Text Card */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: isReversed ? -60 : 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              style={{ height: '100%' }}
            >
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  p: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.light',
                    boxShadow: '0 0 0 3px rgba(var(--mui-palette-primary-mainChannel) / 0.15)',
                  },
                }}
              >
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {item.title}
                </Typography>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  {item.features.map((feat, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: isReversed ? -20 : 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.35, ease: 'easeOut', delay: i * 0.08 }}
                    >
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <CheckCircle sx={{ color: 'success.main', fontSize: 20, mt: '2px', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">
                          {feat}
                        </Typography>
                      </Box>
                    </motion.div>
                  ))}
                </Stack>
              </Paper>
            </motion.div>
          </Grid2>
        </Grid2>
      );
    })}
  </Box>
);

const VideoShowcase = ({ payload }) => {
  const [activeTab, setActiveTab] = useState(0);
  return (
    <Box sx={{ py: 4 }}>
      <Grid2 container spacing={4}>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Stack spacing={2}>
            {payload.map((video, idx) => (
              <Card 
                key={idx} 
                component={motion.div}
                whileHover={{ scale: 1.02 }}
                onClick={() => setActiveTab(idx)}
                sx={{ 
                  cursor: 'pointer', borderLeft: 6, 
                  borderColor: activeTab === idx ? 'secondary.main' : 'transparent',
                  bgcolor: activeTab === idx ? 'action.selected' : 'background.paper',
                }}
              >
                <CardContent>
                  <Typography variant="overline" color="primary" fontWeight={700}>{video.header}</Typography>
                  <Typography variant="h6">{video.title}</Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 8 }}>
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <Paper elevation={12} sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: 'black' }}>
                <video autoPlay muted loop playsInline src={payload[activeTab].link} style={{ width: '100%', display: 'block' }} />
              </Paper>
            </motion.div>
          </AnimatePresence>
        </Grid2>
      </Grid2>
    </Box>
  );
};


// --- MAIN HOMEPAGE ---

const HomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ pt: 15, pb: 10, textAlign: 'center', background: `radial-gradient(circle at 50% 0%, ${theme.palette.primary.light}15, transparent 50%)` }}>
        <Container maxWidth="md">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <Typography variant="h2" fontWeight={900} sx={{ mb: 3 }}>Deliver educational services effortlessly</Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 5 }}>Easy monitoring, powerful analytics, and data logs within seconds.</Typography>
            <Button variant="contained" size="large" onClick={() => navigate('/register')} endIcon={<ArrowForward />} sx={{ px: 5, py: 2, borderRadius: 10, fontWeight: 700 }}>Get Started</Button>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Admin Dashboard Video */}
        <Box sx={{ mb: 15 }}>
          <Divider sx={{ mb: 8 }}><Chip label="Real-time Platform" color="info" /></Divider>
          <Paper elevation={0} sx={{ borderRadius: 6, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <video autoPlay muted loop playsInline src={admin_data.link} style={{ width: '100%' }} />
          </Paper>
        </Box>

        {/* Features Grid */}
        <Grid2 container spacing={3} sx={{ mb: 15 }}>
          {primaryFeatures.map((item, index) => (
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <Paper sx={{
                  p: 4, height: '100%',
                  transition: '0.3s',
                  borderTop: '3px solid',
                  borderColor: `${item.color}.main`,       // ← color accent per card
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 }
                }}>
                <Box sx={{
                    width: 48, height: 48, borderRadius: '50%',
                    bgcolor: `${item.color}.50`,             // very light tint
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: `${item.color}.main`,
                    mb: 2
                  }}>
                    {item.icon}
                  </Box>
                <Typography variant="h6" fontWeight={700}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
                {item.description}</Typography>
              </Paper>
            </Grid2>
          ))}
        </Grid2>
        <br /> 

        <ValidationInsightsSection />       

        {/* Oversight Section */}
        <Divider sx={{ mb: 8 }}><Chip label="In-Depth Monitoring" color="info" /></Divider>
        <FeatureSlide payload={tutor_data_} />
        <FeatureSlide payload={student_data_} />

        {/* Video Showcase */}
        <Divider sx={{ my: 12 }}><Chip label="Sneak Peek" color="info" /></Divider>
        <VideoShowcase payload={video_data_} />

        {/* Organization Architecture */}
        <Divider sx={{ my: 12 }}><Chip label="Data Architecture" color="info" /></Divider>
        <Grid2 container spacing={8} alignItems="center" sx={{ mb: 15 }}>
          <Grid2 size={{ xs: 12, md: 7 }}><Paper elevation={8} sx={{ borderRadius: 4, overflow: 'hidden' }}><img src={organization_data.link} alt="Org" style={{ width: '100%' }} /></Paper></Grid2>
          <Grid2 size={{ xs: 12, md: 5 }}>
            <Typography variant="h3" fontWeight={800} gutterBottom>{organization_data.title}</Typography>
            <Stack spacing={3}>
              {organization_data.features.map((feat, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ minWidth: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mt: 1.2 }} />
                  <Typography variant="body1" color="text.secondary">{feat}</Typography>
                </Box>
              ))}
            </Stack>
          </Grid2>
        </Grid2>
      </Container>
      <Footer />
    </Box>
  );
};

export default HomePage;